import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePoll } from '../contexts/PollContext';
import { ChevronRight, ChevronLeft, Trash2, Loader, Eye, RefreshCw, Pause, Play, Coins } from 'lucide-react';
import MatchSettingsComponent from './MatchSettingsComponent';
import TeamFormation from '../teams/components/TeamFormation';
import { getScoringComponent } from './scoringComponents';
import PauseGameModal from './components/PauseGameModal';
import PausedBanner from './components/PausedBanner';
import CoinFlipModal from './components/CoinFlipModal';
import { apiCall } from '../services/apiUtil';
import { scrollToTop } from '../utils/helpers';

// Generic games API endpoint (refactored to use unified /games endpoint)
const GAMES_ENDPOINT = '/games';

/**
 * GameComponent - Orchestrates the complete game flow
 *
 * Flow:
 * 1. Match Settings → configure game rules
 * 2. Team Formation → select participants
 * 3. Scoring → play the game
 *
 * @param {string} sportType - Sport type (e.g., 'ping pong', 'tennis', 'basketball')
 * @param {number} gameCommonId - The game_common record ID
 * @param {number} groupId - Group ID (for fetching existing teams in TeamFormation)
 * @param {Array} members - Available members for team selection
 * @param {Object} event - Event object (for checking event status)
 * @param {Function} onGameComplete - Callback when game is finished
 * @param {Function} onCancel - Callback to cancel and go back
 * @param {string} backLabel - Label for the persistent header back button (defaults to "Back")
 */
const GameComponent = ({
  sportType,
  sportTypeId,
  maxPlayersPerSide = null,
  gameCommonId: initialGameCommonId,
  groupId,
  members = [],
  event,
  onGameComplete,
  onCancel,
  backLabel = 'Back',
  // Scorer prop: true = this user is the dedicated scorer, false = viewer (read-only),
  // null/undefined = determine from loaded game data
  isScorer: isScoreProp = undefined,
  isAdmin = false,
  // Tournament-specific props
  initialParticipantA = null,
  initialParticipantB = null,
  // Full team rosters (unnarrowed by any per-match player subset) — temp
  // debug verification + future mid-match player swap UI
  participantATeamRoster = [],
  participantBTeamRoster = [],
  initialGameSettings = null
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();

  // Poker is attendee-based — no team formation needed
  const isPoker = sportTypeId === 1;

  // Poker skips settings entirely — start directly at scoring
  const [currentStep, setCurrentStep] = useState(isPoker ? 'scoring' : 'settings'); // 'settings' | 'formation' | 'scoring'
  const [loading, setLoading] = useState(true);
  // null = unknown (game loading), true = no points yet, false = points recorded
  const [canNavigateBack, setCanNavigateBack] = useState(null);
  const rootRef = useRef(null);

  // Resolved scoring component — getScoringComponent is async so we store the result in state.
  // undefined = still loading, null = not found, function = ready to render
  const [scoringComponent, setScoringComponent] = useState(undefined);
  useEffect(() => {
    if (!sportTypeId) { setScoringComponent(null); return; }
    setScoringComponent(undefined); // reset to loading when sportTypeId changes
    getScoringComponent(sportTypeId).then(component => {
      // React's useState treats function arguments as state updaters (calling fn(prevState)).
      // Since scoring components ARE functions, we must wrap them to store as values, not call them.
      setScoringComponent(component ? () => component : null);
    });
  }, [sportTypeId]);

  // Scorer state: true = this user is the scorer, false = viewer (read-only)
  // Only explicit true/false from parent overrides; null/undefined = auto-detect from game data in loadGame
  const [isScorer, setIsScorer] = useState(isScoreProp === true || isScoreProp === false ? isScoreProp : true);
  const [scorerInfo, setScorerInfo] = useState(null);
  // Refresh key for viewer mode — increments when poll delivers fresh game data
  const [refreshKey, setRefreshKey] = useState(0);

  // Delete game confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [gameDeleted, setGameDeleted] = useState(false);

  // Pause / resume — centralized here so each sport's scoring component only
  // has to honor the `paused` prop. The actual paused state is read from the
  // poll context (pollGames[gameCommonId]) so it stays in sync if another
  // device pauses the same game.
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [pauseError, setPauseError] = useState(null);

  // Coin toss — UI only for now; result is not persisted yet.
  const [showTossModal, setShowTossModal] = useState(false);

  // Game common ID (can be set after creation)
  const [gameCommonId, setGameCommonId] = useState(initialGameCommonId);

  // Register this game with the poll context so it gets refreshed every 10s
  const { registerGame, unregisterGame, games: pollGames, patchGame } = usePoll();
  useEffect(() => {
    if (gameCommonId) {
      registerGame(gameCommonId);
      return () => unregisterGame(gameCommonId);
    }
  }, [gameCommonId, registerGame, unregisterGame]);

  // Keep isScorer in sync when parent explicitly sets true/false
  // null/undefined means "auto-detect from game data" — don't override
  useEffect(() => {
    if (isScoreProp === true || isScoreProp === false) {
      setIsScorer(isScoreProp);
    }
  }, [isScoreProp]);

  // Poll-driven updates: re-check scorer role and refresh viewer display.
  // Runs for ALL users so that a user who initially loaded with no scorer
  // gets demoted to viewer once another user claims the scorer role.
  const prevPollGameRef = useRef(null);
  useEffect(() => {
    if (currentStep === 'scoring' && gameCommonId) {
      const polledGame = pollGames[String(gameCommonId)];
      if (polledGame && polledGame !== prevPollGameRef.current) {
        prevPollGameRef.current = polledGame;

        // Re-evaluate scorer role from polled game data (unless parent explicitly set it)
        // Use == for comparison since scorerId (number) and user.id (may be string) can differ in type
        if (isScoreProp !== true && isScoreProp !== false) {
          const pollScorerId = polledGame.scorerId;
          const isActiveGame = polledGame.status !== 'completed';
          // Once the game is completed there's no concurrent-scoring risk left —
          // the only action still gated on isScorer is "Complete Event & Continue".
          // Anyone who reached this screen already passed the tournament-level
          // scorer check upstream (only scorers get a clickable Live button), so
          // don't keep the exclusivity lock tied to whichever scorer_id happened
          // to score the game — that stranded completed games when the original
          // scorer's session dropped before they hit "Complete Event".
          const isMeScorer = !isActiveGame || pollScorerId == user?.id || (!pollScorerId && isActiveGame);

          if (isMeScorer && !isScorer) {
            setIsScorer(true);
            setScorerInfo(null);
          } else if (!isMeScorer && isScorer) {
            // Another user claimed scorer — demote this user to viewer
            setIsScorer(false);
            setScorerInfo(polledGame.scorer || null);
          }

          // For viewers (including freshly demoted), bump refreshKey to remount with fresh data.
          // Also bump when game completes so the scoring component re-mounts and detects completed status.
          if (!isMeScorer) {
            setRefreshKey(k => k + 1);
          }
        }
      }
    }
  }, [pollGames, isScorer, isScoreProp, currentStep, gameCommonId, user?.id]);

  // Collected data from each step
  const [gameSettings, setGameSettings] = useState(initialGameSettings);
  const [participantsType, setParticipantsType] = useState(initialGameSettings?.participantsType || initialGameSettings?.format || 'singles');
  const [participants, setParticipants] = useState({
    participant_a_info: initialParticipantA,
    participant_b_info: initialParticipantB
  });

  /**
   * Load game and determine current step based on status
   */
  useEffect(() => {
    let cancelled = false; // guard against stale async updates after unmount/dep change

    const loadGame = async () => {
      // Get sport-specific endpoint
      const endpoint = GAMES_ENDPOINT;

      if (!gameCommonId) {
        // No gameCommonId provided - check if there's an existing game for this event
        if (event?.id) {

          try {
            const games = await apiCall(`${GAMES_ENDPOINT}/event/${event.id}`);

            if (cancelled) return;


            // Find any existing game for this event (including completed — no second game allowed)
            const existingGame = games.find(g =>
              g.status === 'in_progress' ||
              g.status === 'active' ||
              g.status === 'scoring' ||
              g.status === 'player_selection' ||
              g.status === 'completed'
            );

            if (existingGame) {
              // Set the gameCommonId and load the game details
              const foundGameId = existingGame.id || existingGame.gameCommonId;
              if (!cancelled) setGameCommonId(foundGameId);

              // Load the full game details immediately
              try {
                const game = await apiCall(`${GAMES_ENDPOINT}/${foundGameId}`);

                if (cancelled) return;


                if (cancelled) return;

                // Set current step based on game status
                // Poker skips settings and formation — always goes to scoring
                const statusToStepMap = {
                  'setup': isPoker ? 'scoring' : 'settings',
                  'player_selection': isPoker ? 'scoring' : 'formation',
                  'in_progress': 'scoring',
                  'active': 'scoring',
                  'scoring': 'scoring',
                  'completed': 'scoring'
                };

                const step = statusToStepMap[game.status] || 'settings';
                setCurrentStep(step);

                // Load existing settings if available — but only past 'setup'.
                // Every sport's transform fills in defaults (e.g. bestOf: 3)
                // even for a brand-new game with no real settings yet, so
                // this condition is basically always true; trusting it for a
                // still-'setup' game would overwrite initialGameSettings
                // (tournament config / per-match override) with those
                // placeholder defaults before the user ever sees the form.
                if (game.status !== 'setup' && (game.pointsToWin || game.bestOf || game.oversPerInning)) {
                  const loadedParticipantsType = initialGameSettings?.participantsType || initialGameSettings?.format || game.participantsType || game.format || 'singles';
                  setParticipantsType(loadedParticipantsType);
                  setGameSettings({
                    participantsType: loadedParticipantsType,
                    maxPoints: game.pointsToWin || 11,
                    winBy2: game.winByTwo !== false,
                    bestOf: game.bestOf || 3,
                    // Cricket-specific settings
                    oversPerInning: game.oversPerInning,
                    oversPerBowler: game.oversPerBowler,
                    inningsPerTeam: game.inningsPerTeam,
                    maxPlayersPerTeam: game.maxPlayersPerTeam
                  });
                }

                // Load existing participants if available
                // Merge with initialParticipant data to preserve player lists (DB may not store them)
                if (game.playerA && game.playerB) {
                  const mergeParticipant = (dbParticipant, initialParticipant) => ({
                    ...dbParticipant,
                    players: dbParticipant.players?.length > 0
                      ? dbParticipant.players
                      : (initialParticipant?.players || [])
                  });
                  setParticipants({
                    participant_a_info: mergeParticipant(game.playerA, initialParticipantA),
                    participant_b_info: mergeParticipant(game.playerB, initialParticipantB)
                  });
                }
              } catch (error) {
                console.error('[GameComponent] Error loading game details:', error);
              }

              if (!cancelled) setLoading(false);
              return;
            } else {
            }
          } catch (error) {
            console.error('[GameComponent] Error checking for existing games:', error);
          }
        }

        // For new games, always start at settings (pre-populated if tournament provides them)
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        let game;
        try {
          game = await apiCall(`${GAMES_ENDPOINT}/${gameCommonId}`);
        } catch (error) {
          // Only navigate back for 404 (game was deleted).
          // For other errors (e.g. 400 validation), stay put to avoid a re-open loop
          // where checkForExistingGames immediately re-opens the same broken game.
          if (!cancelled && onCancel && error.status === 404) onCancel();
          return;
        }

        if (cancelled) return;

        if (cancelled) return;

        // Determine scorer role from game data when parent didn't set an explicit true/false
        if (isScoreProp !== true && isScoreProp !== false) {
          const scorerId = game.scorerId;
          const isMe = scorerId == user?.id;
          // null scorer_id on a completed game means no one is actively scoring — treat as viewer
          const isActiveGame = game.status !== 'completed';
          if (isMe || (!scorerId && isActiveGame)) {
            setIsScorer(true);
          } else {
            setIsScorer(false);
            setScorerInfo(game.scorer || null);
          }
        }

        // Set current step based on game status
        // Poker skips settings and formation — always goes to scoring
        const statusToStepMap = {
          'setup': isPoker ? 'scoring' : 'settings',
          'player_selection': isPoker ? 'scoring' : 'formation',
          'in_progress': 'scoring',
          'active': 'scoring',
          'scoring': 'scoring',
          'completed': 'scoring'
        };

        const step = statusToStepMap[game.status] || 'settings';
        setCurrentStep(step);

        // Load existing settings if available — but only once the game has
        // actually moved past 'setup'. createGame() always seeds
        // game_settings with at least { format: 'singles' } even when the
        // caller (e.g. starting scoring from the tournament schedule) sends
        // no settings at all, so a brand-new 'setup' game's gameSettings is
        // never truly empty. Trusting it here would silently overwrite
        // initialGameSettings — the tournament's own config or a per-match
        // override — with that placeholder before the user ever sees the
        // settings form. Once the game is past 'setup', gameSettings reflects
        // what the user actually confirmed via handleSettingsComplete, so it's
        // safe (and correct) to load.
        if (game.status !== 'setup' && game.gameSettings && Object.keys(game.gameSettings).length > 0) {
          const loadedParticipantsType = initialGameSettings?.participantsType || initialGameSettings?.format || game.gameSettings.participantsType || game.gameSettings.format || 'singles';
          setParticipantsType(loadedParticipantsType);
          setGameSettings({
            ...game.gameSettings,
            participantsType: loadedParticipantsType,
          });
        }

        // Load existing participants if available
        // Merge with initialParticipant data to preserve player lists (DB may not store them)
        if (game.playerA && game.playerB) {
          const mergeParticipant = (dbParticipant, initialParticipant) => ({
            ...dbParticipant,
            players: dbParticipant.players?.length > 0
              ? dbParticipant.players
              : (initialParticipant?.players || [])
          });
          setParticipants({
            participant_a_info: mergeParticipant(game.playerA, initialParticipantA),
            participant_b_info: mergeParticipant(game.playerB, initialParticipantB)
          });
        }
      } catch (error) {
        console.error('[GameComponent] Failed to load game:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadGame();
    return () => { cancelled = true; };
  }, [initialGameCommonId, sportType, event?.id]);

  // Auto-skip Settings + Team Formation when opened with everything already
  // known — the tournament schedule's "Start Scoring" flow always creates
  // the game_common up front (still 'setup') and passes real
  // initialParticipantA/B + initialGameSettings, so there's nothing left for
  // the user to actually choose on either screen; making them click through
  // both anyway is pure friction. Writes the exact same fields
  // handleSettingsComplete/handleFormationComplete would have (gameSettings,
  // format, playerA, playerB), just in one PATCH straight to 'in_progress'
  // instead of pausing at the intermediate 'player_selection' status nobody
  // is actually choosing anything at. Scoped tightly to the tournament case:
  // `gameCommonId` must already exist (never true for a fresh ad-hoc game
  // from My Games etc., which only gets one once the user completes
  // Formation) and both initialParticipantA/B + initialGameSettings must be
  // real — anything else falls through to the normal manual flow untouched.
  useEffect(() => {
    if (loading || isPoker || currentStep !== 'settings' || !gameCommonId) return;
    const hasTeams = !!(initialParticipantA?.id || initialParticipantA?.players?.length) &&
      !!(initialParticipantB?.id || initialParticipantB?.players?.length);
    const hasSettings = !!(initialGameSettings && Object.keys(initialGameSettings).length > 0);
    if (!hasTeams || !hasSettings) return;

    let cancelled = false;
    (async () => {
      try {
        await apiCall(`${GAMES_ENDPOINT}/${gameCommonId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            gameSettings: initialGameSettings,
            format: initialGameSettings.participantsType || initialGameSettings.format,
            playerA: initialParticipantA,
            playerB: initialParticipantB,
            status: 'in_progress',
          }),
        });
        if (cancelled) return;
        setParticipants({ participant_a_info: initialParticipantA, participant_b_info: initialParticipantB });
        setGameSettings(initialGameSettings);
        if (initialGameSettings.participantsType) setParticipantsType(initialGameSettings.participantsType);
        setCurrentStep('scoring');
      } catch (error) {
        console.error('[GameComponent] Failed to auto-skip settings/formation:', error);
        // Leave currentStep at 'settings' so the user can still complete it manually.
      }
    })();
    return () => { cancelled = true; };
  }, [loading, isPoker, currentStep, gameCommonId, initialParticipantA, initialParticipantB, initialGameSettings]);

  /**
   * Handle completion of match settings step
   */
  const handleSettingsComplete = async (settings) => {
    // settings already includes participantsType from MatchSettingsComponent
    setGameSettings(settings);
    if (settings.participantsType) setParticipantsType(settings.participantsType);

    // Get sport-specific endpoint
    const endpoint = GAMES_ENDPOINT;

    // Update game_common with settings (skip for viewers — navigation only)
    if (gameCommonId && isScorer !== false) {
      try {
        const updatedGame = await apiCall(`${GAMES_ENDPOINT}/${gameCommonId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            gameSettings: settings,
            format: settings.participantsType,
            status: 'player_selection'
          })
        });
      } catch (error) {
        console.error('[GameComponent] Failed to update game settings:', error);
      }
    }

    setCurrentStep('formation');
  };

  /**
   * Convert teams from TeamFormation format to participant format
   */
  const convertTeamsToParticipants = (teamsData) => {
    const { teams } = teamsData;

    if (!teams || teams.length < 2) {
      console.error('[GameComponent] Invalid teams data:', teamsData);
      return null;
    }

    const formatParticipant = (team) => {
      const formattedPlayers = team.players.map(p => ({
        id: p.id,
        name: p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()
      }));

      // Single player (singles)
      if (team.players.length === 1) {
        const player = team.players[0];
        return {
          type: 'user',
          id: player.id,
          name: player.name || `${player.firstName} ${player.lastName}`.trim(),
          players: formattedPlayers
        };
      }

      // Team/doubles (multiple players)
      return {
        type: 'team',
        id: team.existingTeamId || team.savedId || null,
        name: team.name,
        players: formattedPlayers
      };
    };

    return {
      participant_a_info: formatParticipant(teams[0]),
      participant_b_info: formatParticipant(teams[1])
    };
  };

  /**
   * Convert participants to teams format for initial render
   */
  const convertParticipantsToTeams = () => {
    if (!participants.participant_a_info && !participants.participant_b_info) {
      return [];
    }

    const formatTeam = (participant, index) => {
      if (!participant) return null;

      if (participant.type === 'user') {
        return {
          name: participant.name,
          players: [{
            id: participant.id,
            name: participant.name
          }],
          existingTeamId: null
        };
      } else {
        // For on-the-fly teams (no saved team id), default to "Team A/B"
        const defaultName = `Team ${String.fromCharCode(65 + index)}`;
        return {
          name: participant.id ? participant.name : defaultName,
          players: participant.players || [],
          existingTeamId: participant.id
        };
      }
    };

    const teams = [];
    const teamA = formatTeam(participants.participant_a_info, 0);
    const teamB = formatTeam(participants.participant_b_info, 1);

    if (teamA) teams.push(teamA);
    if (teamB) teams.push(teamB);

    return teams.length > 0 ? teams : null;
  };

  /**
   * Handle completion of team formation step
   */
  const handleFormationComplete = async (teamsData) => {

    const participantData = convertTeamsToParticipants(teamsData);

    if (participantData) {
      setParticipants(participantData);

      // Get games endpoint
      const endpoint = GAMES_ENDPOINT;

      // Create or update game_common with participant data
      if (gameCommonId) {
        // Update existing game_common
        try {
          const updatedGame = await apiCall(`${GAMES_ENDPOINT}/${gameCommonId}`, {
            method: 'PATCH',
            body: JSON.stringify({
              playerA: participantData.participant_a_info,
              playerB: participantData.participant_b_info,
              status: 'in_progress'
            })
          });
        } catch (error) {
          console.error('[GameComponent] Failed to update game participants:', error);
        }
      } else {
        // Create new game_common record
        try {

          if (!sportTypeId) {
            throw new Error('sportTypeId is required to create a game');
          }

          // Pass all settings from MatchSettingsComponent directly
          // The settings are already sport-specific (created by MatchSettingsComponent)
          const createPayload = {
            eventId: event?.id,
            sportTypeId: sportTypeId,
            playerA: participantData.participant_a_info,
            playerB: participantData.participant_b_info,
            status: 'in_progress',
            format: gameSettings?.participantsType || gameSettings?.format || 'singles',
            gameSettings: gameSettings
            // gameName omitted — backend generates "X vs Y" from playerA/playerB
          };


          const newGame = await apiCall(GAMES_ENDPOINT, {
            method: 'POST',
            body: JSON.stringify(createPayload)
          });

          // Set the gameCommonId for the scoring component
          setGameCommonId(newGame.id);
        } catch (error) {
          console.error('[GameComponent] Failed to create game:', error);
          alert('Failed to create game. Please try again.');
          return; // Don't proceed to scoring if creation failed
        }
      }

      setCurrentStep('scoring');
    } else {
      console.error('[GameComponent] Failed to convert teams to participants - staying on formation');
      console.error('[GameComponent] teamsData.teams:', teamsData?.teams);
    }
  };

  /**
   * Handle game completion from scoring component
   */
  const handleScoringComplete = async (gameData) => {
    // Completing a game happens at the bottom of a long scroll, and whatever
    // replaces it — the result summary, the schedule, the next fixture — starts
    // at the top. Captured before awaiting, since the handler may unmount this.
    const el = rootRef.current;
    try {
      if (onGameComplete) {
        return await onGameComplete(gameData);
      }
    } finally {
      scrollToTop(el);
    }
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    if (currentStep === 'formation') {
      setCurrentStep('settings');
    } else if (currentStep === 'scoring') {
      if (isPoker) {
        // Poker has no settings/formation steps — back goes to cancel
        if (onCancel) onCancel();
      } else {
        setCurrentStep('formation');
      }
    } else if (onCancel) {
      onCancel();
    }
  };

  /**
   * Navigate directly back to match settings from scoring
   */
  const handleBackToSettings = () => {
    setCurrentStep('settings');
  };

  /**
   * Pause / resume the current game. Hits the shared /games/:id/pause and
   * /games/:id/resume endpoints — sport-agnostic on the wire. The polled
   * gameCommon.status flips to 'paused' / 'in_progress' and propagates as
   * the `paused` prop into whichever scoring component is mounted.
   */
  const handlePauseConfirm = async ({ reason, durationMinutes }) => {
    if (!gameCommonId) return;
    setIsPausing(true);
    setPauseError(null);
    try {
      const result = await apiCall(`${GAMES_ENDPOINT}/${gameCommonId}/pause`, {
        method: 'POST',
        body: JSON.stringify({ reason, durationMinutes }),
      });
      // Optimistically flip the polled state so the banner appears and the
      // scoring UI disables immediately, instead of waiting for the next 10s
      // poll tick to fetch the new status.
      patchGame(gameCommonId, {
        status: 'paused',
        pause_reason: result?.pause_reason ?? null,
        auto_resume_at: result?.auto_resume_at ?? null,
      });
      setShowPauseModal(false);
    } catch (err) {
      console.error('[GameComponent] Error pausing game:', err);
      setPauseError(err.message || 'Failed to pause game.');
    } finally {
      setIsPausing(false);
    }
  };

  const handleResumeGame = async () => {
    if (!gameCommonId) return;
    setIsResuming(true);
    try {
      await apiCall(`${GAMES_ENDPOINT}/${gameCommonId}/resume`, { method: 'POST' });
      // Optimistic flip — same reason as pause above.
      patchGame(gameCommonId, {
        status: 'in_progress',
        pause_reason: null,
        auto_resume_at: null,
      });
    } catch (err) {
      console.error('[GameComponent] Error resuming game:', err);
    } finally {
      setIsResuming(false);
    }
  };

  /**
   * Delete the current game and return to event info
   */
  const handleDeleteGame = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      if (gameCommonId) {
        await apiCall(`${GAMES_ENDPOINT}/${gameCommonId}`, { method: 'DELETE' });
      }
      setGameCommonId(null); // clear retained ID so PokerScoring won't reuse it
      setGameDeleted(true);
      setShowDeleteConfirm(false);
      if (onCancel) onCancel();
    } catch (err) {
      console.error('[GameComponent] Error deleting game:', err);
      setDeleteError(err.message || 'Failed to delete game. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Render the current step
   */
  const renderStep = () => {
    switch (currentStep) {
      case 'settings':
        return (
          <MatchSettingsComponent
            sportType={sportType}
            initialSettings={gameSettings}
            onNext={handleSettingsComplete}
            readOnly={isScorer === false}
            showNextButton={true}
            participantsTypeValue={participantsType}
            onParticipantsTypeChange={setParticipantsType}
          />
        );

      case 'formation': {
        // Determine game type from format
        const formatToGameType = {
          'singles': 'single',
          'doubles': 'double',
          'teams': 'team'
        };
        const gameType = formatToGameType[participantsType] || 'team';

        const maxPlayersPerTeam = maxPlayersPerSide || undefined;

        return (
          <div>
            {/* Back Button */}
            <div className="mb-4">
              <button
                onClick={handleBack}
                className={`px-4 py-2 rounded-lg border ${theme.border} ${theme.text} ${theme.cardHover} transition-colors
                  flex items-center gap-2 font-medium`}
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Settings</span>
              </button>
            </div>

            {/* TeamFormation Component */}
            <TeamFormation
              availableMembers={members}
              ownerId={groupId || user?.id}
              ownerType={groupId ? "group" : "user"}
              gameType={gameType}
              maxPlayersPerTeam={maxPlayersPerTeam}
              teamCount={2}
              initialTeams={convertParticipantsToTeams()}
              showConfirmButton={isScorer !== false}
              confirmButtonText="Start Scoring"
              onTeamsFormed={handleFormationComplete}
              saveToBackend={false}
              readOnly={!!(initialParticipantA && initialParticipantB) || isScorer === false}
              showReadOnlyMessage={!!(initialParticipantA && initialParticipantB)}
              showSettingsInfo={true}
              sportName={sportType}
              showExistingTeams={gameType === 'team' || gameType === 'double'}
            />
          </div>
        );
      }

      case 'scoring': {
        if (gameDeleted) return null;


        const ScoringComponent = scoringComponent;

        // Still resolving the component asynchronously — show a spinner
        if (ScoringComponent === undefined) {
          return (
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
            </div>
          );
        }

        // Fetch completed but no component found for this sport
        if (!ScoringComponent) {
          return (
            <div className="p-8 text-center">
              <p className={theme.text}>No scoring component available for this sport (sportTypeId: {sportTypeId}).</p>
            </div>
          );
        }

        const scorerDisplayName = scorerInfo
          ? `${scorerInfo.first_name || ''} ${scorerInfo.last_name || ''}`.trim()
          : 'Another user';

        return (
          <div>
            {/* Viewer mode banner */}
            {!isScorer && (
              <div className="flex items-center gap-2 mx-4 mt-4 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-950 border border-amber-300 dark:border-amber-700">
                <Eye className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                <span className="text-sm text-amber-800 dark:text-amber-300 flex-1">
                  <span className="font-semibold">{scorerDisplayName}</span> is scoring — you're in view-only mode
                </span>
                <RefreshCw className="w-3.5 h-3.5 text-amber-500 animate-spin" style={{ animationDuration: '3s' }} />
              </div>
            )}
            {/* Rendered only when it can actually be used. `invisible` merely
                hides it — the box stays, so once the first point was scored
                every sport carried ~86px of dead space above the scoreboard for
                the rest of the game. */}
            {!isPoker && canNavigateBack === true && (
              <div className="flex gap-2 mb-4 px-4 pt-4">
                <button
                  onClick={handleBackToSettings}
                  disabled={canNavigateBack !== true}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border ${theme.border} ${theme.text}
                    hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm font-medium`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Match Settings
                </button>
                <button
                  onClick={handleBack}
                  disabled={canNavigateBack !== true}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border ${theme.border} ${theme.text}
                    hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm font-medium`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Team Selection
                </button>
              </div>
            )}
            {/* Centralized pause banner */}
            {isPaused && (
              <div className="px-4 pt-4">
                <PausedBanner
                  pauseReason={polledGame?.pause_reason}
                  autoResumeAt={polledGame?.auto_resume_at}
                  isScorer={isScorer}
                  onResume={handleResumeGame}
                  resuming={isResuming}
                />
              </div>
            )}
            {/* VIEWERS CANNOT TOUCH THE SCOREBOARD, WHATEVER THE SPORT.
                Each sport component already takes `isScorer` and greys out the
                controls it knows about, but that is eight components deciding
                the same thing separately — and a control somebody forgets stays
                live. `inert` settles it in one place for every sport, present
                and future: the whole subtree stops taking clicks, keystrokes
                and focus, so a viewer cannot score even through a button nobody
                remembered to disable.
                The server refuses them anyway (assertScorer on game_common), so
                this is the half that stops them trying and being confused by a
                403 they cannot explain. */}
            <div inert={isScorer === false || undefined}>
              <ScoringComponent
                key={isScorer ? 'scorer' : `viewer-${refreshKey}`}
                gameCommonId={gameCommonId}
                gameSettings={gameSettings}
                participantAInfo={participants.participant_a_info}
                participantBInfo={participants.participant_b_info}
                participantATeamRoster={participantATeamRoster}
                participantBTeamRoster={participantBTeamRoster}
                event={event}
                members={members}
                isScorer={isScorer}
                paused={isPaused}
                sportType={sportType}
                onGameComplete={isScorer ? handleScoringComplete : undefined}
                onCancel={isScorer ? onCancel : undefined}
                onBack={isScorer ? handleBack : undefined}
                onBackToSettings={isScorer ? handleBackToSettings : undefined}
                onFirstPointScored={isScorer ? () => setCanNavigateBack(false) : undefined}
                onGamePointsStatus={isScorer ? (hasPoints) => setCanNavigateBack(!hasPoints) : undefined}
              />
            </div>
          </div>
        );
      }

      default:
        return <div>Invalid step</div>;
    }
  };

  // Progress indicator — poker skips settings and teams
  const steps = isPoker
    ? [
        { key: 'scoring', label: 'Score' }
      ]
    : [
        { key: 'settings', label: 'Settings' },
        { key: 'formation', label: 'Teams' },
        { key: 'scoring', label: 'Score' }
      ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  // Pause state derived from the poll context — single source of truth.
  const polledGame = gameCommonId ? pollGames[gameCommonId] : null;
  const isPaused = polledGame?.status === 'paused';
  const isCompleted = polledGame?.status === 'completed';
  // Hide Pause on completed games — pausing a finished game is meaningless
  // and would put game_common into a contradictory paused/completed state.
  const showPauseControls = currentStep === 'scoring' && gameCommonId && !gameDeleted && !isCompleted;

  // Show loading state while fetching game
  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`min-h-screen ${theme.background}`}>
      {/* Progress Indicator */}
      <div className={`${theme.card} border-b ${theme.border}`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2 min-w-0">
            {/* Back — always available since leaving this screen never discards
                game progress, it just switches which outer tab is showing;
                unlike the step-navigation Back buttons below this isn't gated
                on canNavigateBack. */}
            {onCancel && (
              <button
                onClick={onCancel}
                className={`flex items-center gap-1 p-1.5 -ml-1.5 rounded-lg ${theme.textMuted} hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0`}
                title={backLabel}
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm font-medium hidden sm:inline">{backLabel}</span>
              </button>
            )}

            {/* Steps */}
            <div className="flex items-center flex-1 min-w-0 overflow-hidden">
              {steps.map((step, index) => (
                <React.Fragment key={step.key}>
                  <div className="flex items-center gap-2">
                    {/* Step Circle */}
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors
                        ${index < currentStepIndex
                          ? 'bg-green-500 text-white'
                          : index === currentStepIndex
                          ? 'bg-blue-600 text-white'
                          : `${theme.backgroundSecondary} ${theme.textMuted}`
                        }`}
                    >
                      {index < currentStepIndex ? '✓' : index + 1}
                    </div>
                    {/* Step Label */}
                    <span
                      className={`text-sm font-medium hidden sm:inline
                        ${index === currentStepIndex ? theme.text : theme.textMuted}`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 rounded transition-colors
                        ${index < currentStepIndex ? 'bg-green-500' : theme.border}`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Toss — scorer only */}
            {isScorer && currentStep === 'scoring' && !gameDeleted && (
              <button
                onClick={() => setShowTossModal(true)}
                className="flex items-center p-1.5 rounded-lg text-yellow-600 border border-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-950 transition-colors shrink-0"
                title="Toss a coin"
              >
                <Coins className="w-4 h-4" />
              </button>
            )}

            {/* Pause / Resume — scorer only, sport must opt into centralized pause */}
            {isScorer && showPauseControls && (
              isPaused ? (
                <button
                  onClick={handleResumeGame}
                  disabled={isResuming}
                  className="flex items-center p-1.5 rounded-lg text-green-600 border border-green-300 hover:bg-green-50 dark:hover:bg-green-950 transition-colors shrink-0 disabled:opacity-60"
                  title="Resume this game"
                >
                  <Play className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => setShowPauseModal(true)}
                  className="flex items-center p-1.5 rounded-lg text-amber-600 border border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950 transition-colors shrink-0"
                  title="Pause this game"
                >
                  <Pause className="w-4 h-4" />
                </button>
              )
            )}

            {/* Delete Game Button — visible to scorer or group admin; hidden on completed games */}
            {(isScorer || isAdmin) && onCancel && !isCompleted && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center p-1.5 rounded-lg text-red-500 border border-red-300 hover:bg-red-50 dark:hover:bg-red-950 transition-colors shrink-0"
                title="Delete this game"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Match Settings Summary — scoring step only */}
      {currentStep === 'scoring' && gameSettings && Object.keys(gameSettings).length > 0 && (
        <div className={`${theme.card} border-b ${theme.border} px-4 py-2`}>
          <div className={`max-w-4xl mx-auto flex flex-wrap gap-x-3 gap-y-1 text-xs ${theme.textMuted}`}>
            {(gameSettings.participantsType || gameSettings.format) && (
              <span className={`${theme.textMuted} capitalize`}>
                {gameSettings.participantsType || gameSettings.format}
              </span>
            )}
            {(gameSettings.maxPoints || gameSettings.scoreToWin) && (
              <span className={theme.textMuted}>· {gameSettings.maxPoints || gameSettings.scoreToWin} pts</span>
            )}
            {gameSettings.bestOf && (
              <span className={theme.textMuted}>· Best of {gameSettings.bestOf}</span>
            )}
            {(gameSettings.isRally || gameSettings.is_rally) && (
              <span className={theme.textMuted}>· Rally scoring</span>
            )}
            {(gameSettings.winBy2 ?? gameSettings.winByTwo) && (
              <span className={theme.textMuted}>· Win by 2</span>
            )}
          </div>
        </div>
      )}

      {/* Coin Toss Modal — UI only, result not yet persisted */}
      <CoinFlipModal
        isOpen={showTossModal}
        onClose={() => setShowTossModal(false)}
      />

      {/* Pause Game Modal — centralized */}
      <PauseGameModal
        isOpen={showPauseModal}
        onClose={() => { setShowPauseModal(false); setPauseError(null); }}
        onConfirm={handlePauseConfirm}
        loading={isPausing}
      />
      {pauseError && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
          {pauseError}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`${theme.card} rounded-2xl shadow-2xl p-6 max-w-sm w-full border ${theme.border}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <h3 className={`text-lg font-semibold ${theme.text}`}>Delete Game?</h3>
            </div>
            <p className={`text-sm ${theme.textMuted} mb-4`}>
              This will permanently delete the game and all recorded scores. This action cannot be undone.
            </p>
            {deleteError && (
              <p className="text-sm text-red-500 mb-4 bg-red-50 dark:bg-red-950 px-3 py-2 rounded-lg">
                {deleteError}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                disabled={isDeleting}
                className={`flex-1 py-2.5 rounded-xl border ${theme.border} ${theme.text} font-medium text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGame}
                disabled={isDeleting}
                className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isDeleting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {isDeleting ? 'Deleting...' : 'Delete Game'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Step Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {renderStep()}
      </div>

    </div>
  );
};

export default GameComponent;
